import { useLingui } from "@lingui/react";
import { useForm, useWatch } from "react-hook-form";
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CompanySelect, OrganizationSelect } from "@/components/common/entity-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { createRecipeSchema, type CreateRecipeFormData } from "./schemas";

interface CreateRecipeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (data: CreateRecipeFormData, companyId: number) => void;
  /** Concept the user has no choice about. Hides the concept select entirely. */
  companyId?: number;
  /**
   * Concept the select opens on while staying changeable. A user who can filter
   * across concepts has picked one on the page behind this dialog; that is a
   * starting point, not a lock, so it must not arrive as `companyId`.
   */
  defaultCompanyId?: number | null;
  organizationId?: number | null;
  canSelectOrganization?: boolean;
  isPending?: boolean;
  /**
   * Type the select opens on. Set when the user already said what they are
   * making (the "Add plated recipe" tile), so the question isn't asked twice.
   */
  defaultRecipeType?: CreateRecipeFormData["recipeType"];
}

export function CreateRecipeDialog({
  open,
  onOpenChange,
  onConfirm,
  companyId,
  defaultCompanyId,
  organizationId,
  canSelectOrganization = false,
  isPending = false,
  defaultRecipeType,
}: CreateRecipeDialogProps) {
  const { i18n } = useLingui();
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(
    organizationId ?? null,
  );
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    companyId ?? defaultCompanyId ?? null,
  );
  const [contextError, setContextError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm<CreateRecipeFormData>({
    resolver: zodResolver(createRecipeSchema),
    defaultValues: {
      name: "",
      // Unset unless the caller already knows: see `createRecipeSchema`. The
      // chef picks.
      recipeType: defaultRecipeType,
      totalYield: undefined,
      // A plated recipe is costed by the plate, so that is its natural yield.
      yieldUnit: defaultRecipeType === "PLATED" ? "plate" : undefined,
    },
  });

  const selectedRecipeType = useWatch({ control, name: "recipeType" });
  const selectedUnit = useWatch({ control, name: "yieldUnit" });

  const onSubmit = (data: CreateRecipeFormData) => {
    const resolvedCompanyId = companyId ?? selectedCompanyId;
    if (!resolvedCompanyId) {
      setContextError(i18n._("Select a concept before creating a recipe."));
      return;
    }

    onConfirm(data, resolvedCompanyId);
    setContextError(null);
    reset();
  };

  const inputClass = "";
  const selectTriggerClass = "";

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        if (!v) {
          setContextError(null);
          setSelectedOrganizationId(organizationId ?? null);
          setSelectedCompanyId(companyId ?? defaultCompanyId ?? null);
        }
        onOpenChange(v);
      }}
    >
      <DialogContent
        hideClose
        className="max-w-[700px] p-0 gap-0 overflow-hidden border border-(--shell-border) rounded-[12px]"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5">
          <div className="flex flex-col gap-2">
            <DialogTitle className="text-2xl font-medium text-(--color-text-primary) leading-6">
              {i18n._("Create recipe")}
            </DialogTitle>
            <p className="text-base text-(--color-text-secondary) font-normal leading-none">
              {i18n._("Create a new recipe and set its basic details.")}
            </p>
          </div>
          <DialogClose
            aria-label={i18n._("Close")}
            className="text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors shrink-0"
          >
            <X className="size-6" />
          </DialogClose>
        </div>

        {/* Form body */}
        <div className="flex flex-col gap-5 px-5 pb-5">
          {!companyId && (
            <div className="grid grid-cols-2 gap-3">
              {canSelectOrganization && (
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium text-(--color-text-primary)">
                    {i18n._("Organization")}
                  </Label>
                  <OrganizationSelect
                    value={selectedOrganizationId}
                    adminScope
                    onChange={(value) => {
                      setSelectedOrganizationId(value);
                      setSelectedCompanyId(null);
                      setContextError(null);
                    }}
                    onClear={() => {
                      setSelectedOrganizationId(null);
                      setSelectedCompanyId(null);
                    }}
                    placeholder={i18n._("Select organization")}
                  />
                </div>
              )}

              {!companyId && (
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium text-(--color-text-primary)">
                    {i18n._("Concept")}
                  </Label>
                  <CompanySelect
                    value={selectedCompanyId}
                    organizationId={selectedOrganizationId ?? organizationId ?? undefined}
                    disabled={canSelectOrganization && !selectedOrganizationId}
                    onChange={(value) => {
                      setSelectedCompanyId(value);
                      setContextError(null);
                    }}
                    onClear={() => {
                      setSelectedCompanyId(null);
                    }}
                    placeholder={i18n._("Select concept")}
                  />
                </div>
              )}
            </div>
          )}

          {/* Recipe name */}
          <div className="flex flex-col gap-2">
            <Label htmlFor="recipe-name" className="text-sm font-medium text-(--color-text-primary)">
              {i18n._("Recipe name")}
            </Label>
            <Input
              id="recipe-name"
              placeholder={i18n._("Enter recipe name...")}
              {...register("name")}
              className={`${inputClass} ${errors.name ? "border-(--color-danger)" : ""}`}
            />
            {errors.name && (
              <p className="text-xs text-(--color-danger-text)">{errors.name.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            {/* Recipe type. A plain <select> rather than the Radix one used
                elsewhere in this dialog: this field decides whether the recipe
                can ever reach a menu, so it is worth having a control that
                keyboards, screen readers and tests all drive the same way. */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="recipe-type" className="text-sm font-medium text-(--color-text-primary)">
                {i18n._("Recipe type")}
              </Label>
              <select
                id="recipe-type"
                aria-label={i18n._("Recipe type")}
                value={selectedRecipeType ?? ""}
                onChange={(e) =>
                  setValue("recipeType", e.target.value as CreateRecipeFormData["recipeType"], {
                    shouldValidate: true,
                  })
                }
                className={`h-9 rounded-md border bg-transparent px-3 text-sm ${
                  errors.recipeType ? "border-(--color-danger)" : "border-(--color-border)"
                }`}
              >
                <option value="" disabled>
                  {i18n._("Select")}
                </option>
                <option value="PREP">{i18n._("Prep recipe")}</option>
                <option value="PLATED">{i18n._("Plated recipe")}</option>
              </select>
              {errors.recipeType ? (
                <p className="text-xs text-(--color-danger-text)">{errors.recipeType.message}</p>
              ) : (
                <p className="text-xs text-(--color-text-secondary)">
                  {i18n._("Only plated recipes can go on a menu. This can't be changed later.")}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {/* Total yield */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="total-yield" className="text-sm font-medium text-(--color-text-primary)">
                {i18n._("Total yield")}
              </Label>
              <Input
                id="total-yield"
                type="number"
                placeholder={i18n._("Enter")}
                {...register("totalYield", {
                  setValueAs: (v: string) => (v === "" ? undefined : Number(v)),
                })}
                className={inputClass}
              />
            </div>

            {/* Yield unit */}
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium text-(--color-text-primary)">
                {i18n._("Yield unit")}
              </Label>
              <Select
                value={selectedUnit ?? ""}
                onValueChange={(v) => setValue("yieldUnit", v)}
              >
                <SelectTrigger className={selectTriggerClass}>
                  <SelectValue placeholder={i18n._("Select")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="plate">{i18n._("plate")}</SelectItem>
                  <SelectItem value="kg">{i18n._("kg")}</SelectItem>
                  <SelectItem value="g">{i18n._("g")}</SelectItem>
                  <SelectItem value="l">{i18n._("l")}</SelectItem>
                  <SelectItem value="ml">{i18n._("ml")}</SelectItem>
                  <SelectItem value="pieces">{i18n._("pieces")}</SelectItem>
                  <SelectItem value="portions">{i18n._("portions")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 pb-5">
          {contextError && (
            <p className="mr-auto text-xs text-(--color-danger-text)">{contextError}</p>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              reset();
              setContextError(null);
              setSelectedOrganizationId(organizationId ?? null);
              setSelectedCompanyId(companyId ?? defaultCompanyId ?? null);
              onOpenChange(false);
            }}
            className="w-[200px] h-[52px] rounded-(--radius-food-thumb) border-(--color-border) text-base font-medium text-(--color-text-primary)"
          >
            {i18n._("Cancel")}
          </Button>
          <Button
            type="button"
            disabled={isPending}
            onClick={handleSubmit(onSubmit)}
            className="w-[200px] h-[52px] rounded-(--radius-food-thumb) bg-(--color-action) text-(--color-action-fg) text-base font-medium hover:bg-(--color-action)/90"
          >
            {i18n._("Confirm")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
